/**
 * Validation tab — the promoted, actionable home for validation results.
 *
 * Reads and MERGES both run docs: validation/latest (waves 0,1,2a,2b,2c) and
 * validation/guide (wave 3). Keeps the by-layer category structure; renders each
 * offending item as its own actionable row using the structured items[].
 *
 * Three actions per decision row:
 *   - Take me there  page-level navigation to the destination screen
 *   - Approve a fix  only F5/F6/E5 (the offered fixes); deletions confirm first
 *   - Dismiss        record a reason; the item drops from the active count and
 *                    shows as declined. Undo re-instates it.
 *
 * Dismissal state is computed LIVE from the validationDismissals collection
 * (key + matching fingerprint), mirroring the server's applyDismissals, so a
 * dismiss/undismiss is reflected instantly without waiting for a re-run.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { onSnapshot, collection } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import {
  CheckCircle2, AlertTriangle, HelpCircle, RefreshCw, Loader2, ArrowRight,
  Wand2, XCircle, RotateCcw, ShieldCheck, ChevronRight, Sparkles,
} from "lucide-react";
import { db, functions } from "../../firebase";
import { CLOUD_FUNCTIONS, CALLABLE_TIMEOUTS } from "../../constants/firebase";
import { validationLatestDoc, validationGuideDoc } from "../../constants/firestorePaths";
import { LAYER_COPY, getCheckCopy, type LayerKey } from "../../features/validation/checkCopy";
import { navTargetFor } from "../../features/validation/navTargets";
import { computeValidationCounts } from "../../features/validation/validationCounts";
import { usePipelineReview } from "../../features/corrections/usePipelineReview";
import PipelineReviewSection from "../../components/corrections/PipelineReviewSection";
import { parseDetailItems, cleanText } from "../../features/validation/detailFormat";
import {
  type AutoFixRecordLike, isDisplayableAutoFix, autoFixSectionCopy,
  autoFixLead, autoFixBadge, autoFixChangeLines,
} from "../../features/validation/autoFixCopy";
import { verificationHeadline } from "../../utils/verificationHeadline";
import { callWithDeadline } from "../../lib/callWithDeadline";
import { useToast } from "../../contexts/ToastContext";
import ConfirmDialog from "../../components/ConfirmDialog";

type Grade = "PASS" | "WARN" | "FAIL";
type Fixability = "auto" | "ask" | "offered" | "platform";

interface FindingItem { subject: string; fingerprint: string; detail: string; applyArgs?: Record<string, string>; }
interface CheckResult { checkId: string; wave: LayerKey; status: Grade; fixability: Fixability; detail: string; items?: FindingItem[]; }
interface OpenDecision {
  key: string; checkId: string; wave: LayerKey; fixability: Fixability;
  subject: string; detail: string; fingerprint: string; dismissed?: boolean;
}
interface ValidationRun {
  completedAt?: { toDate?: () => Date; seconds?: number };
  wavesRun?: LayerKey[];
  checks?: CheckResult[];
  openDecisions?: OpenDecision[];
  autoFixes?: AutoFixRecordLike[];
}
interface Dismissal { key: string; fingerprint: string; reason: string; dismissedBy: string; dismissedAt?: { toDate?: () => Date }; }

interface Row {
  key: string; checkId: string; wave: LayerKey; fixability: Fixability;
  subject: string; detail: string; fingerprint: string; status: Grade;
  applyArgs?: Record<string, string>;
  dismissed: boolean;
  dismissal?: Dismissal;
}
interface InfoRow {
  checkId: string; wave: LayerKey; fixability: Fixability;
  detail: string; itemDetails: string[];
}

/**
 * How long the page waits for a re-run before telling the user it has stopped
 * waiting. NOT the SDK's `timeout` — that one starts only after the auth-token
 * fetch, so a hung token fetch is unbounded by it (see lib/callWithDeadline.ts).
 *
 * 90 s against a measured worst case of 26.8 s end to end on 2026-09-02 — of
 * which 15.3 s was two CORS preflights cold-starting their containers. Roughly
 * 3x headroom over the slowest real run observed.
 */
const RERUN_DEADLINE_MS = 90_000;

const LAYER_ORDER: LayerKey[] = ["0", "1", "2a", "2b", "2c", "3"];
const APPROVABLE = new Set(["F5", "F6", "E5"]);
const DESTRUCTIVE = new Set(["F6", "E5"]);

// Common dismiss reasons. Selecting one satisfies the required reason; "Other"
// reveals a free-text field that is then required.
const OTHER_REASON = "Other";
const DISMISS_REASONS = [
  "Equivalent / just reworded",
  "Intentional design choice",
  "Not applicable to this study",
  OTHER_REASON,
] as const;

function relativeTime(d: Date | null): string {
  if (!d) return "";
  const secs = Math.max(1, Math.round((Date.now() - d.getTime()) / 1000));
  if (secs < 60) return `${secs}s ago`;
  const m = Math.round(secs / 60);
  if (m < 60) return `${m} minute${m === 1 ? "" : "s"} ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} hour${h === 1 ? "" : "s"} ago`;
  return `${Math.round(h / 24)} day${Math.round(h / 24) === 1 ? "" : "s"} ago`;
}

export default function ValidationTab() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();

  // Pipeline-family decisions (inc5 step F) — same hook the nav chip uses.
  const pipeline = usePipelineReview(projectId);

  const [latest, setLatest] = useState<ValidationRun | null>(null);
  const [guide, setGuide] = useState<ValidationRun | null>(null);
  const [dismissals, setDismissals] = useState<Map<string, Dismissal>>(new Map());
  const [loaded, setLoaded] = useState(false);
  const [rerunning, setRerunning] = useState(false);
  /**
   * When the last manual re-run finished, or null when there is nothing to
   * acknowledge. Drives the completion panel below the header.
   *
   * THIS REPLACED A 4-SECOND SUCCESS TOAST. On 2026-09-02 Jim clicked Re-run
   * and reported that nothing happened. Everything had happened — four 2xx
   * Cloud Run requests, both run records written — but the confirmation was a
   * `success` toast, which auto-dismisses after 4000 ms, in the bottom-left
   * corner, ~900px from the top-right button he was watching, for an action
   * that took 26.8 seconds. He read the page 1.1 seconds before it expired.
   *
   * So this is state, not a notification: it is cleared by the user, or
   * replaced when the next run starts. Never by a timer.
   */
  const [runComplete, setRunComplete] = useState<Date | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  // Pending confirmations / reason prompt.
  const [approveTarget, setApproveTarget] = useState<Row | null>(null);
  const [dismissTarget, setDismissTarget] = useState<Row | null>(null);
  const [dismissChoice, setDismissChoice] = useState("");   // selected dropdown reason
  const [dismissReason, setDismissReason] = useState("");   // free text, used only for "Other"
  // Per-area collapse state. Undefined = use the default, which is COLLAPSED:
  // a finished run presents as a clean per-area summary (name, checks-clear
  // line, count badge) and Jim expands only the areas he wants to read.
  // An explicit true/false here is Jim's own toggle and always wins — until a
  // fresh run lands, at which point the overrides are cleared (see below).
  const [openAreas, setOpenAreas] = useState<Partial<Record<LayerKey, boolean>>>({});

  useEffect(() => {
    if (!projectId) return;
    let seen = 0;
    const mark = () => { seen++; if (seen >= 1) setLoaded(true); };
    const u1 = onSnapshot(validationLatestDoc(projectId), (s) => { setLatest(s.exists() ? (s.data() as ValidationRun) : null); mark(); }, () => mark());
    const u2 = onSnapshot(validationGuideDoc(projectId), (s) => setGuide(s.exists() ? (s.data() as ValidationRun) : null), () => {});
    const u3 = onSnapshot(collection(db, "projects", projectId, "validationDismissals"), (snap) => {
      const m = new Map<string, Dismissal>();
      snap.forEach((d) => { const x = d.data() as Dismissal; if (x?.key) m.set(x.key, x); });
      setDismissals(m);
    }, () => {});
    return () => { u1(); u2(); u3(); };
  }, [projectId]);

  // Merge both runs into actionable rows; compute dismissed state LIVE.
  const rows = useMemo<Row[]>(() => {
    const out: Row[] = [];
    for (const run of [latest, guide]) {
      if (!run) continue;
      const argsByKey = new Map<string, Record<string, string> | undefined>();
      const statusByCheck = new Map<string, Grade>();
      for (const c of run.checks || []) {
        statusByCheck.set(c.checkId, c.status);
        for (const it of c.items || []) argsByKey.set(`${c.checkId}:${it.subject}`, it.applyArgs);
      }
      for (const d of run.openDecisions || []) {
        const rec = dismissals.get(d.key);
        const dismissed = !!rec && rec.fingerprint === d.fingerprint;
        out.push({
          key: d.key, checkId: d.checkId, wave: d.wave, fixability: d.fixability,
          subject: d.subject, detail: d.detail, fingerprint: d.fingerprint,
          status: statusByCheck.get(d.checkId) || "WARN",
          applyArgs: argsByKey.get(d.key),
          dismissed, dismissal: dismissed ? rec : undefined,
        });
      }
    }
    return out;
  }, [latest, guide, dismissals]);

  // Automatic corrections — the disclosure surface for auto-fix records. These
  // are NOT decisions and NOT counted (see computeValidationCounts); they report
  // what the system corrected (apply) or would correct (observe). Empty -> the
  // section renders nothing at all. Mode is uniform within a run.
  const autoFixRows = useMemo<AutoFixRecordLike[]>(() => {
    const out: AutoFixRecordLike[] = [];
    for (const run of [latest, guide]) {
      if (!run) continue;
      for (const f of run.autoFixes || []) {
        if (isDisplayableAutoFix(f)) out.push(f);
      }
    }
    return out;
  }, [latest, guide]);

  // A check that has an auto-fix record is disclosed ONLY in the corrections
  // surface above — never also as a generic info row (that would double-render
  // the same finding). J6 in observe therefore appears exactly once.
  const autoFixedCheckIds = useMemo(
    () => new Set(autoFixRows.map((f) => f.checkId)),
    [autoFixRows],
  );
  const autoFixMode: "observe" | "apply" = autoFixRows.some((f) => f.mode === "apply") ? "apply" : "observe";

  // Non-actionable findings — auto WITHOUT a fix record, and platform (tracked).
  // One row per check; not decisions, so not in openDecisions and not dismissable.
  const infoRows = useMemo<InfoRow[]>(() => {
    const out: InfoRow[] = [];
    for (const run of [latest, guide]) {
      if (!run) continue;
      for (const c of run.checks || []) {
        if (c.status === "PASS") continue;
        if (c.fixability !== "auto" && c.fixability !== "platform") continue;
        if (autoFixedCheckIds.has(c.checkId)) continue; // shown in the corrections surface instead
        out.push({
          checkId: c.checkId, wave: c.wave, fixability: c.fixability,
          detail: c.detail,
          itemDetails: (c.items || []).map((i) => i.detail),
        });
      }
    }
    return out;
  }, [latest, guide, autoFixedCheckIds]);

  // Derive the headline counts from the SAME shared source the nav chip uses, so
  // the two can never disagree. (Equivalent to counting `rows`, but single-sourced.)
  // Inc5 step F: the pipeline family's open decisions join the same number.
  const counts = useMemo(
    () => computeValidationCounts(latest, guide, dismissals, { openCount: pipeline.openCount }),
    [latest, guide, dismissals, pipeline.openCount],
  );
  const activeCount = counts.active;
  const declinedCount = counts.declined;

  // Per-wave passed/total for the confidence-first header line.
  const waveSummary = useMemo(() => {
    const m = new Map<LayerKey, { passed: number; total: number; ran: boolean }>();
    for (const run of [latest, guide]) {
      if (!run) continue;
      for (const c of run.checks || []) {
        const e = m.get(c.wave) || { passed: 0, total: 0, ran: true };
        e.total++; if (c.status === "PASS") e.passed++;
        m.set(c.wave, e);
      }
    }
    return m;
  }, [latest, guide]);

  /**
   * When the WHOLE re-run finished — the later of the two completion stamps.
   *
   * A re-run calls `runValidationV2` then `runPipelineVerificationV2`, and the
   * second finishes about 13 seconds after the first (measured 2026-09-02:
   * validation 17:56:20.582, pipeline 17:56:35.283). Reading only
   * `validation/latest.completedAt` meant the counter was already at "16s ago"
   * at the instant the run actually completed, so the "Last run" line and the
   * completion panel described different events while looking like one.
   *
   * Both documents expose the field: `validation/latest.completedAt`
   * (runValidation.ts) and `pipelineVerification/latest.completedAt`
   * (pipelineVerification.ts) — VERIFIED in code and by reading both live
   * documents on 2026-09-02.
   */
  const pipelineCompletedAt = pipeline.completedAt;
  const lastRun = useMemo(() => {
    const v = latest?.completedAt?.toDate?.() || null;
    const p = pipelineCompletedAt;
    if (v && p) return p.getTime() > v.getTime() ? p : v;
    return v ?? p ?? null;
  }, [latest, pipelineCompletedAt]);

  // When a fresh run lands, drop any manual expand/collapse choices so the new
  // results present collapsed. Keyed on each run doc's completion stamp, so it
  // fires for automatic (trigger-driven) runs too, not just the Re-run button.
  const latestStamp = latest?.completedAt?.seconds ?? null;
  const guideStamp = guide?.completedAt?.seconds ?? null;
  useEffect(() => { setOpenAreas({}); }, [latestStamp, guideStamp]);

  async function reRun() {
    if (!projectId) return;
    // A new run replaces the previous acknowledgement rather than stacking on it.
    setRunComplete(null);
    setRerunning(true);
    try {
      // Both engines re-run: the wave checks and the cross-document
      // consistency checks (inc5 step F) — one button, whole picture.
      //
      // SEQUENTIAL, DELIBERATELY. These two do not look ordered and are.
      // `runValidationV2`'s auto-fix phase WRITES documents the pipeline run
      // READS: F7 writes `dataManagement/dataDictionary` and its `entries`
      // (autofix/fixers/f7FormDdCoverage.ts), J6 writes
      // `dataManagement/editCheckSpec` (fixers/j6SpecSummary.ts), and
      // pipelineVerification.ts reads all three. Run them together and the
      // pipeline checks read a data dictionary mid-write — a torn read of
      // exactly the counts they exist to assert. Verified 2026-09-02; do not
      // "optimise" this into a Promise.all without re-checking those paths.
      await callWithDeadline(
        () => httpsCallable(functions, CLOUD_FUNCTIONS.RUN_VALIDATION_V2, { timeout: CALLABLE_TIMEOUTS.MEDIUM })({ projectId }),
        RERUN_DEADLINE_MS,
        "The verification run",
      );
      await callWithDeadline(
        () => httpsCallable(functions, CLOUD_FUNCTIONS.RUN_PIPELINE_VERIFICATION_V2, { timeout: CALLABLE_TIMEOUTS.MEDIUM })({ projectId }),
        RERUN_DEADLINE_MS,
        "The cross-document check",
      );
      // Acknowledged in place, below the header, until the user dismisses it —
      // NOT a toast. See the `runComplete` declaration for why.
      setRunComplete(new Date());
    } catch (e: any) {
      // Error toasts are PERSISTENT (ToastContext DEFAULT_TIMEOUT_MS.error = 0),
      // so a deadline message stays until the user dismisses it.
      showToast(`Could not re-run verification: ${e?.message || "unknown error"}`, "error");
    } finally {
      setRerunning(false);
    }
  }

  function goThere(row: Row) {
    const t = navTargetFor(row.checkId);
    navigate(`/project/${projectId}/${t.route}${t.query ? `?${t.query}` : ""}`);
  }

  async function doApprove(row: Row) {
    if (!projectId) return;
    setBusyKey(row.key);
    try {
      let name: string = CLOUD_FUNCTIONS.APPLY_DD_FORM_NAME_RENAME_V2;
      let args: Record<string, unknown> = {};
      if (row.checkId === "F5") {
        name = CLOUD_FUNCTIONS.APPLY_DD_FORM_NAME_RENAME_V2;
        args = { projectId, from: row.subject, to: row.applyArgs?.to };
      } else if (row.checkId === "F6") {
        name = CLOUD_FUNCTIONS.APPLY_DD_ORPHAN_REMOVAL_V2;
        args = { projectId, formName: row.subject };
      } else if (row.checkId === "E5") {
        name = CLOUD_FUNCTIONS.APPLY_DUPLICATE_ARTIFACT_REMOVAL_V2;
        args = { projectId, artifactId: row.subject };
      }
      const res: any = (await httpsCallable(functions, name, { timeout: CALLABLE_TIMEOUTS.MEDIUM })(args)).data;

      // The apply paths CAN refuse (data changed, now ambiguous) — surface it.
      if (res && res.applied === false) {
        showToast(res.refusedReason || "The fix could not be applied.", "error");
        return;
      }
      showToast("Fix applied — the previous version was archived and is recoverable.", "success");
      // Refresh the run so the tab reflects the change (E5 does not fire a trigger).
      httpsCallable(functions, CLOUD_FUNCTIONS.RUN_VALIDATION_V2, { timeout: CALLABLE_TIMEOUTS.MEDIUM })({ projectId }).catch(() => {});
    } catch (e: any) {
      showToast(`Could not apply the fix: ${e?.message || "unknown error"}`, "error");
    } finally {
      setBusyKey(null);
      setApproveTarget(null);
    }
  }

  // Effective reason = the free text when "Other" is chosen, else the dropdown label.
  function effectiveDismissReason(): string {
    return dismissChoice === OTHER_REASON ? dismissReason.trim() : dismissChoice.trim();
  }

  async function doDismiss(row: Row) {
    if (!projectId) return;
    const reason = effectiveDismissReason();
    if (!reason) { showToast("A reason is required to dismiss a finding.", "warning"); return; }
    setBusyKey(row.key);
    try {
      await httpsCallable(functions, CLOUD_FUNCTIONS.DISMISS_VALIDATION_FINDING_V2, { timeout: CALLABLE_TIMEOUTS.SHORT })({
        projectId, checkId: row.checkId, subject: row.subject, fingerprint: row.fingerprint, reason,
      });
      showToast("Finding declined.", "success", { action: { label: "Undo", onClick: () => void undoDismiss(row) } });
    } catch (e: any) {
      showToast(`Could not record the dismissal: ${e?.message || "unknown error"}`, "error");
    } finally {
      setBusyKey(null);
      setDismissTarget(null);
      setDismissChoice("");
      setDismissReason("");
    }
  }

  async function undoDismiss(row: Row) {
    if (!projectId) return;
    try {
      await httpsCallable(functions, CLOUD_FUNCTIONS.UNDISMISS_VALIDATION_FINDING_V2, { timeout: CALLABLE_TIMEOUTS.SHORT })({
        projectId, key: row.key,
      });
      showToast("Finding restored.", "success");
    } catch (e: any) {
      showToast(`Could not restore the finding: ${e?.message || "unknown error"}`, "error");
    }
  }

  if (!loaded) {
    return (
      <div className="flex items-center justify-center py-24 text-gray-500">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading verification…
      </div>
    );
  }

  const neverRun = !latest && !guide;

  // One place decides the wording AND the icon. Inline, the zero-count branch
  // printed a green tick and "All checks are clear." on a study where nothing
  // had ever been checked — see `verificationHeadline.test.ts`.
  const headline = verificationHeadline({ activeCount, declinedCount, neverRun });

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <ShieldCheck className="h-6 w-6 text-indigo-600" /> Verification
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Automatic checks across your study documents. {lastRun ? `Last run ${relativeTime(lastRun)}.` : ""}
          </p>
        </div>
        <button
          onClick={reRun}
          disabled={rerunning}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          {rerunning ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          Re-run
        </button>
      </div>

      {/* In-flight run indicator.
          INDETERMINATE by design: the run executes all its waves in memory and
          writes only once, at the end (runValidation.ts), so the client has no
          per-wave signal to advance a bar against. Rather than animate a
          made-up percentage, this shows motion without implying progress. It
          unmounts when the callable resolves and the fresh results render. */}
      {rerunning && (
        <div
          role="status"
          aria-busy="true"
          className="rounded-xl border border-indigo-100 bg-indigo-50/40 p-4 mb-6 animate-fade-in"
        >
          <div className="flex items-start gap-3">
            <Loader2 className="h-5 w-5 text-indigo-500 animate-spin shrink-0 mt-0.5" />
            <div className="min-w-0">
              <div className="font-medium text-gray-900">Running verification…</div>
              <div className="text-sm text-gray-500 mt-0.5">
                {/* TODO(2026-09-02): "about 20 seconds" is a guess and the one
                    measurement we have says 27 — 26.8s end to end on
                    rhM8u18ub8ONo5YkUXj8, of which 15.3s was two CORS preflights
                    cold-starting. Correct this copy from the Cloud Run timings
                    of the next few real runs rather than re-guessing it. */}
                Re-checking your study documents. This usually takes about 20 seconds — the
                results below will refresh on their own.
              </div>
            </div>
          </div>
          <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-indigo-100" aria-hidden="true">
            <div className="h-full w-1/3 rounded-full bg-indigo-500 animate-indeterminate" />
          </div>
        </div>
      )}

      {/* Completion panel — the acknowledgement, in the place the action was
          taken and the banner above it occupied.
          It is STATE, NOT A NOTIFICATION: it leaves when the user dismisses it
          or when the next run starts, never on a timer. The toast it replaced
          lived 4 seconds, in the opposite corner, for a 27-second action. */}
      {!rerunning && runComplete && (
        <div
          role="status"
          className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-4 mb-6 animate-fade-in flex items-start gap-3"
        >
          <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <div className="font-medium text-gray-900">Verification complete</div>
            <div className="text-sm text-gray-600 mt-0.5">
              Finished at {runComplete.toLocaleTimeString()}. The results below are up to date.
            </div>
          </div>
          <button
            onClick={() => setRunComplete(null)}
            aria-label="Dismiss"
            className="shrink-0 p-1 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XCircle className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Headline tally */}
      <div className="rounded-xl border border-gray-200 bg-white p-4 mb-6 flex items-center gap-4">
        {headline.tone === "clear" && <CheckCircle2 className="h-8 w-8 text-emerald-500" />}
        {headline.tone === "attention" && <AlertTriangle className="h-8 w-8 text-amber-500" />}
        {headline.tone === "neutral" && <HelpCircle className="h-8 w-8 text-gray-400" />}
        <div>
          <div className="font-medium text-gray-900">{headline.title}</div>
          <div className="text-sm text-gray-500">{headline.subtitle}</div>
        </div>
      </div>

      {/* Automatic corrections — only rendered when there is at least one. */}
      {autoFixRows.length > 0 && (
        <AutoFixSection records={autoFixRows} mode={autoFixMode} />
      )}

      {/* Pipeline-family decisions (inc5 step F): the AESEV-class offers,
          mapping-ambiguity and DD-staleness guidance, and the auto-clear
          disclosure — the routing-split home, replacing the EDC Build card. */}
      {projectId && <PipelineReviewSection projectId={projectId} data={pipeline} />}

      {/* The headline above already says this, in the place a reader looks
          first. Repeating it here said the same sentence twice on one screen. */}

      {/* Per-category (layer) accordions — restores the old card's grouping. */}
      <div className="space-y-3">
        {LAYER_ORDER.map((wave) => {
          const summary = waveSummary.get(wave);
          if (!summary) return null; // wave not run
          const waveRows = rows
            .filter((r) => r.wave === wave)
            .slice()
            .sort((a, b) => Number(a.dismissed) - Number(b.dismissed));
          const waveInfo = infoRows.filter((r) => r.wave === wave);
          const activeN = waveRows.filter((r) => !r.dismissed).length;
          const declinedN = waveRows.filter((r) => r.dismissed).length;
          const isOpen = openAreas[wave] ?? false;
          const nothingHere = waveRows.length === 0 && waveInfo.length === 0;

          const badge =
            activeN > 0
              ? { cls: "bg-amber-100 text-amber-800 ring-1 ring-amber-700/20", text: `${activeN} to review` }
              : declinedN > 0
                ? { cls: "bg-gray-100 text-gray-500 ring-1 ring-gray-400/20", text: `${declinedN} declined` }
                : { cls: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-600/20", text: "All clear" };

          return (
            <details
              key={wave}
              open={isOpen}
              onToggle={(e) => {
                const el = (e.currentTarget || e.target) as HTMLDetailsElement | null;
                const open = !!el?.open;
                setOpenAreas((s) => ({ ...s, [wave]: open }));
              }}
              className={"group rounded-xl bg-white border " + (activeN > 0 ? "border-amber-200" : "border-gray-200")}
            >
              <summary className="flex items-center justify-between gap-2 px-4 py-3 cursor-pointer list-none [&::-webkit-details-marker]:hidden">
                <div className="min-w-0 flex items-center gap-1.5">
                  <ChevronRight className="h-4 w-4 text-gray-400 shrink-0 transition-transform group-open:rotate-90" />
                  <div className="min-w-0">
                    <div className="font-medium text-gray-800 text-sm truncate">{LAYER_COPY[wave].name}</div>
                    <div className="text-xs text-gray-400">{summary.passed} of {summary.total} checks clear</div>
                  </div>
                </div>
                <span className={"shrink-0 text-[11px] font-medium px-2 py-0.5 rounded-full " + badge.cls}>{badge.text}</span>
              </summary>

              <div className="px-4 pb-4">
                <p className="text-xs text-gray-500 mb-3">{LAYER_COPY[wave].blurb}</p>
                {nothingHere ? (
                  <div className="flex items-center gap-2 text-sm text-emerald-600 bg-emerald-50 rounded-lg px-3 py-2">
                    <CheckCircle2 className="h-4 w-4" /> Nothing to review in this area.
                  </div>
                ) : (
                  <ul className="space-y-2">
                    {waveRows.map((row) => (
                      <RowCard
                        key={row.key}
                        row={row}
                        busy={busyKey === row.key}
                        navLabel={navTargetFor(row.checkId).label}
                        onNavigate={() => goThere(row)}
                        onApprove={() => setApproveTarget(row)}
                        onDismiss={() => { setDismissTarget(row); setDismissChoice(""); setDismissReason(""); }}
                        onUndo={() => void undoDismiss(row)}
                      />
                    ))}
                    {waveInfo.map((info) => (
                      <InfoRowCard key={`${info.checkId}:info`} info={info} />
                    ))}
                  </ul>
                )}
              </div>
            </details>
          );
        })}
      </div>

      {/* Approve confirm — F5 light, F6/E5 destructive */}
      {approveTarget && (
        <ConfirmDialog
          title={
            approveTarget.checkId === "F5" ? "Rename this dictionary form?"
              : approveTarget.checkId === "F6" ? "Remove these dictionary entries?"
                : "Remove this duplicate form?"
          }
          destructive={DESTRUCTIVE.has(approveTarget.checkId)}
          confirmLabel={approveTarget.checkId === "F5" ? "Rename" : "Remove"}
          busy={busyKey === approveTarget.key}
          onCancel={() => setApproveTarget(null)}
          onConfirm={() => void doApprove(approveTarget)}
        >
          {approveTarget.checkId === "F5" ? (
            <p>
              Rename <span className="font-medium">“{approveTarget.subject}”</span> to{" "}
              <span className="font-medium">“{approveTarget.applyArgs?.to}”</span>. The previous value is
              recorded and can be reversed.
            </p>
          ) : approveTarget.checkId === "F6" ? (
            <>
              <p>This will remove the orphaned dictionary entries for <span className="font-medium">“{approveTarget.subject}”</span> — a form that no longer exists.</p>
              <p className="text-emerald-700">Every removed row is archived first and can be recovered.</p>
            </>
          ) : (
            <>
              <p>This will remove the duplicate form artifact <span className="font-medium">“{approveTarget.applyArgs?.deleteName || approveTarget.subject}”</span>{approveTarget.applyArgs?.keepName ? <>, keeping <span className="font-medium">“{approveTarget.applyArgs.keepName}”</span></> : null}.</p>
              <p className="text-emerald-700">The full form is archived first and can be recovered.</p>
            </>
          )}
        </ConfirmDialog>
      )}

      {/* Dismiss reason prompt */}
      {dismissTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setDismissTarget(null)}>
          <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Decline this finding</h3>
            <p className="text-sm text-gray-600 mb-3">{getCheckCopy(dismissTarget.checkId).title} — <span className="text-gray-500">{dismissTarget.detail}</span></p>
            <label className="block text-sm font-medium text-gray-700 mb-1">Reason <span className="text-red-500">*</span></label>
            <select
              value={dismissChoice}
              onChange={(e) => setDismissChoice(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-3 bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="" disabled>Select a reason…</option>
              {DISMISS_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            {dismissChoice === OTHER_REASON && (
              <textarea
                value={dismissReason}
                onChange={(e) => setDismissReason(e.target.value)}
                rows={3}
                placeholder="Describe the reason…"
                autoFocus
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-1 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            )}
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setDismissTarget(null)} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button
                onClick={() => void doDismiss(dismissTarget)}
                disabled={!effectiveDismissReason() || busyKey === dismissTarget.key}
                className="px-4 py-2 text-sm font-medium text-white bg-gray-800 hover:bg-gray-900 rounded-lg disabled:opacity-50 inline-flex items-center gap-2"
              >
                {busyKey === dismissTarget.key && <Loader2 className="h-4 w-4 animate-spin" />}
                Decline
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── One decision row ─────────────────────────────────────────────────────────
function RowCard({
  row, busy, navLabel, onNavigate, onApprove, onDismiss, onUndo,
}: {
  row: Row; busy: boolean; navLabel: string;
  onNavigate: () => void; onApprove: () => void; onDismiss: () => void; onUndo: () => void;
}) {
  const copy = getCheckCopy(row.checkId);
  const approvable = APPROVABLE.has(row.checkId);

  // Lead with the plain-language explanation; render the specifics as a clean
  // list. A per-item (converted) row already IS one item — show its cleaned
  // detail; a lumped row's detail is parsed into a list.
  const specifics = row.subject
    ? { items: [cleanText(row.detail)], moreCount: 0 }
    : parseDetailItems(row.detail);

  const badge = row.dismissed
    ? { label: "Declined", cls: "bg-gray-100 text-gray-500 ring-1 ring-gray-400/20" }
    : row.status === "FAIL"
      ? { label: "Needs your decision", cls: "bg-amber-100 text-amber-800 ring-1 ring-amber-700/20" }
      : { label: "To confirm", cls: "bg-amber-50 text-amber-700 ring-1 ring-amber-600/20" };

  return (
    <li className={"rounded-lg border px-4 py-3 " + (row.dismissed ? "border-gray-200 bg-gray-50" : "border-amber-200 bg-white")}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className={"text-[11px] font-medium px-2 py-0.5 rounded-full " + badge.cls}>{badge.label}</span>
            <span className="text-sm font-medium text-gray-900 truncate">{copy.title}</span>
          </div>
          <p className={"text-sm mt-1 " + (row.dismissed ? "text-gray-400" : "text-gray-700")}>{copy.flag}</p>
          {copy.thin && !row.dismissed && (
            <p className="text-xs text-gray-400 mt-0.5">{copy.thin}</p>
          )}
          <ItemList items={specifics.items} moreCount={specifics.moreCount} muted={row.dismissed} />
          {row.dismissed && row.dismissal?.reason && (
            <p className="text-xs text-gray-400 mt-2 italic">Declined: “{row.dismissal.reason}”</p>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-3">
        {row.dismissed ? (
          <button onClick={onUndo} className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 hover:text-gray-900 border border-gray-300 rounded-md px-2 py-1">
            <RotateCcw className="h-3.5 w-3.5" /> Restore
          </button>
        ) : (
          <>
            <button onClick={onNavigate} className="inline-flex items-center gap-1 text-xs font-medium text-indigo-700 hover:text-indigo-900 border border-indigo-200 rounded-md px-2 py-1">
              {navLabel} <ArrowRight className="h-3.5 w-3.5" />
            </button>
            {approvable && (
              <button onClick={onApprove} disabled={busy} className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:text-emerald-900 border border-emerald-200 rounded-md px-2 py-1 disabled:opacity-50">
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />} Approve fix
              </button>
            )}
            <button onClick={onDismiss} className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-800 border border-gray-200 rounded-md px-2 py-1">
              <XCircle className="h-3.5 w-3.5" /> Decline
            </button>
          </>
        )}
      </div>
    </li>
  );
}

// ── A collapsible, scannable list of offending items ─────────────────────────
function ItemList({ items, moreCount, muted }: { items: string[]; moreCount: number; muted?: boolean }) {
  const [expanded, setExpanded] = useState(false);
  if (items.length === 0 && moreCount === 0) return null;
  const SHOW = 8;
  const shown = expanded ? items : items.slice(0, SHOW);
  const hiddenInline = items.length - shown.length;
  return (
    <ul className={"mt-2 space-y-1 " + (muted ? "text-gray-400" : "text-gray-600")}>
      {shown.map((it, i) => (
        <li key={i} className="text-xs flex gap-1.5 break-words">
          <span className="text-gray-300 mt-0.5 shrink-0">•</span>
          <span className="min-w-0 break-words">{it}</span>
        </li>
      ))}
      {(hiddenInline > 0 || moreCount > 0) && (
        <li className="text-xs pl-3">
          {hiddenInline > 0 && !expanded && (
            <button onClick={() => setExpanded(true)} className="text-indigo-600 hover:text-indigo-800 font-medium">
              Show {hiddenInline} more
            </button>
          )}
          {moreCount > 0 && (
            <span className="text-gray-400 italic">
              {hiddenInline > 0 && !expanded ? " · " : ""}and {moreCount} more not shown here
            </span>
          )}
        </li>
      )}
    </ul>
  );
}

// ── Automatic corrections surface (mode-aware; never counted) ────────────────
function AutoFixSection({ records, mode }: { records: AutoFixRecordLike[]; mode: "observe" | "apply" }) {
  const copy = autoFixSectionCopy(mode);
  return (
    <div className="rounded-xl border border-blue-100 bg-blue-50/40 p-4 mb-6">
      <div className="flex items-start gap-3">
        <Sparkles className="h-5 w-5 text-blue-500 mt-0.5 shrink-0" />
        <div className="min-w-0">
          <div className="font-medium text-gray-900 flex items-center gap-2">
            {copy.title}
            <span className={"text-[11px] font-medium px-2 py-0.5 rounded-full " +
              (mode === "apply" ? "bg-emerald-100 text-emerald-800 ring-1 ring-emerald-700/20"
                                : "bg-blue-100 text-blue-700 ring-1 ring-blue-600/20")}>
              {mode === "apply" ? "Applied" : "Preview mode"}
            </span>
          </div>
          <p className="text-sm text-gray-600 mt-0.5">{copy.blurb}</p>
        </div>
      </div>
      <ul className="space-y-2 mt-3">
        {records.map((r, i) => <AutoFixCard key={`${r.checkId}:${r.subject}:${i}`} rec={r} />)}
      </ul>
    </div>
  );
}

function AutoFixCard({ rec }: { rec: AutoFixRecordLike }) {
  const copy = getCheckCopy(rec.checkId);
  const badge = autoFixBadge(rec);
  const lines = autoFixChangeLines(rec.before, rec.after);
  const badgeCls =
    badge.tone === "done" ? "bg-emerald-100 text-emerald-800 ring-1 ring-emerald-700/20"
      : badge.tone === "proposed" ? "bg-blue-100 text-blue-700 ring-1 ring-blue-600/20"
        : "bg-gray-100 text-gray-500 ring-1 ring-gray-400/20";
  return (
    <li className="rounded-lg border border-blue-100 bg-white px-4 py-3">
      <div className="flex items-center gap-2">
        <span className={"text-[11px] font-medium px-2 py-0.5 rounded-full " + badgeCls}>{badge.label}</span>
        <span className="text-sm font-medium text-gray-800 truncate">{copy.title}</span>
      </div>
      <p className="text-sm mt-1 text-gray-700">{autoFixLead(rec)}</p>
      {lines.length > 0 && (
        <ul className="mt-2 space-y-1 text-gray-600">
          {lines.map((ln, i) => (
            <li key={i} className="text-xs flex gap-1.5 break-words">
              <span className="text-gray-300 mt-0.5 shrink-0">•</span>
              <span className="min-w-0 break-words">{ln}</span>
            </li>
          ))}
        </ul>
      )}
      {rec.status === "applied" && (
        <p className="text-xs text-emerald-700 mt-2">The previous values were recorded and can be reversed.</p>
      )}
    </li>
  );
}

// ── A non-actionable info row (auto-corrected / platform-tracked) ─────────────
function InfoRowCard({ info }: { info: InfoRow }) {
  const copy = getCheckCopy(info.checkId);
  const specifics = info.itemDetails.length > 0
    ? { items: info.itemDetails.map(cleanText), moreCount: 0 }
    : parseDetailItems(info.detail);
  const badge = info.fixability === "auto"
    ? { label: "We'll correct this", cls: "bg-blue-50 text-blue-700 ring-1 ring-blue-600/20" }
    : { label: "We're looking into this", cls: "bg-gray-100 text-gray-500 ring-1 ring-gray-400/20" };
  return (
    <li className="rounded-lg border border-gray-200 bg-gray-50/60 px-4 py-3">
      <div className="flex items-center gap-2">
        <span className={"text-[11px] font-medium px-2 py-0.5 rounded-full " + badge.cls}>{badge.label}</span>
        <span className="text-sm font-medium text-gray-700 truncate">{copy.title}</span>
      </div>
      <p className="text-sm mt-1 text-gray-600">{copy.flag}</p>
      <ItemList items={specifics.items} moreCount={specifics.moreCount} muted />
    </li>
  );
}
